import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Button } from 'reactstrap';
import { isMobile, isIOS, isSafari } from '../../utils/deviceDetection';

/**
 * Manual Install Link Component
 * Shows a clickable link on mobile devices to manually trigger PWA installation
 * Works for both Android and iOS devices
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const ManualInstallLink: React.FC = () => {
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Listen for the beforeinstallprompt event (Android)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    // iOS Safari - show instructions modal
    if (isIOS() && isSafari()) {
      setShowIOSModal(true);
      return;
    }

    // Android - trigger the install prompt
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`[Manual Install] User response: ${outcome}`);
      setDeferredPrompt(null);
    } else {
      // Fallback: show iOS modal for any device without prompt
      setShowIOSModal(true);
    }
  };

  // Only show on mobile devices
  if (!isMobile()) {
    return null;
  }

  return (
    <>
      <div className="mt-3 text-center">
        <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>
          📱{' '}
          <button
            type="button"
            onClick={handleInstallClick}
            className="btn btn-link text-primary text-decoration-underline fw-semibold p-0"
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: 'inherit'
            }}
          >
            Instalar como aplicativo
          </button>
        </p>
      </div>

      {/* iOS Instructions Modal */}
      <Modal
        isOpen={showIOSModal}
        toggle={() => setShowIOSModal(false)}
        centered
      >
        <ModalHeader toggle={() => setShowIOSModal(false)}>
          Instalar ProMédico Contabilidade
        </ModalHeader>
        <ModalBody>
          <div className="text-center mb-3">
            <img
              src="/logo192.png"
              alt="ProMédico Logo"
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '16px',
              }}
            />
          </div>

          <h6 className="mb-3">Como instalar no seu dispositivo:</h6>

          {isIOS() && isSafari() ? (
            // iOS Safari specific instructions
            <div>
              <ol className="ps-3">
                <li className="mb-2">
                  Toque no botão <strong>Compartilhar</strong>{' '}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    style={{ display: 'inline', verticalAlign: 'middle' }}
                  >
                    <path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.11 0-2-.9-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .89 2 2z" />
                  </svg>{' '}
                  na barra inferior
                </li>
                <li className="mb-2">
                  Role para baixo e selecione{' '}
                  <strong>"Adicionar à Tela de Início"</strong>
                </li>
                <li className="mb-2">
                  Toque em <strong>"Adicionar"</strong> para confirmar
                </li>
              </ol>
              <div className="alert alert-info mt-3" role="alert">
                <small>
                  <strong>Nota:</strong> Esta opção só está disponível no Safari.
                  Se estiver usando outro navegador, abra este site no Safari.
                </small>
              </div>
            </div>
          ) : (
            // Generic instructions for other devices
            <div>
              <p className="mb-3">
                Para instalar este aplicativo no seu dispositivo:
              </p>
              <ol className="ps-3">
                <li className="mb-2">
                  Abra o <strong>menu do navegador</strong> (⋮ ou ☰)
                </li>
                <li className="mb-2">
                  Procure por <strong>"Adicionar à tela inicial"</strong> ou{' '}
                  <strong>"Instalar aplicativo"</strong>
                </li>
                <li className="mb-2">
                  Toque para instalar
                </li>
              </ol>
              <div className="alert alert-info mt-3" role="alert">
                <small>
                  O aplicativo aparecerá na sua tela inicial para acesso rápido!
                </small>
              </div>
            </div>
          )}

          <div className="text-center mt-4">
            <Button
              color="primary"
              onClick={() => setShowIOSModal(false)}
            >
              Entendi
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ManualInstallLink;
